/**
 * Entity Type Detection Utilities
 * 
 * Pure functions for detecting entity type categories based on entity type string.
 * These are used by GenericEntityUpsertDialog and other components to determine
 * special handling for different entity types.
 * 
 * Two usage patterns are supported:
 * 1. Generic: isEntityType(entityType, 'set') - single import, flexible, type-safe with autocomplete
 * 2. Specific: isSet(entityType) - readable, type-safe, routes through generic
 *
 * Person detection uses tier1 cached entitlements (EntitySets.Person); no hardcoded fallback.
 */
import EntitlementsService from '../services/EntitlementsService';
import { getPersonEntityTypes } from '../types/EntityEntitlements';

/**
 * Known entity types - centralized enum-like const for type safety and autocomplete
 * Use EntityType.Set, EntityType.Workflow, etc. for type-safe references
 */
export const EntityType = {
    // Core entity types
    Set: 'set',
    Group: 'group',
    Photo: 'photo',

    // Workflow-related
    Workflow: 'workflow',
    WorkflowActivity: 'workflowactivity',
    SetTransition: 'settransition',

    // Policy & access
    PolicyRule: 'policyrule',
    RequestApprovalType: 'requestapprovaltype',
    RequestApproval: 'requestapproval',
    ApiUser: 'apiuser',

    // Communication
    EmailTemplate: 'emailtemplate',
    Content: 'content',
    File: 'file',
    SpooledEmail: 'spooledemail',

    // Person types
    Person: 'person',
    Employee: 'employee',
    Contractor: 'contractor',
    Student: 'student',
    Teacher: 'teacher',
    Staff: 'staff',

    // Locality types
    School: 'school',
    Building: 'building',
    ResponsibilityCenter: 'responsibilitycenter',
    Class: 'class',

    // Audit
    JournalEntry: 'journalentry',
    ModificationError: 'modificationerror',

    // Schema types
    SchemaEntity: 'schemaentity',
    SchemaAttribute: 'schemaattribute',

    // Infrastructure
    AppRegistration: 'appregistration',
    Credential: 'credential',
} as const;

/**
 * Type derived from EntityType values - use for type-safe targetType parameter
 * Provides autocomplete: 'set' | 'group' | 'workflow' | ...
 */
export type KnownEntityType = typeof EntityType[keyof typeof EntityType];

/**
 * Entity type flags for special handling
 */
export interface EntityTypeFlags {
    isSetTransition: boolean;
    isEmailTemplate: boolean;
    isRequestApprovalType: boolean;
    isPolicyRule: boolean;
    isPerson: boolean;
    isWorkflow: boolean;
    isWorkflowActivity: boolean;
    isSet: boolean;
    isGroup: boolean;
    isPhoto: boolean;
    isContent: boolean;
    isFile: boolean;
    isJournalEntry: boolean;
    isSpooledEmail: boolean;
    isRequestApproval: boolean;
    isApiUser: boolean;
    isModificationError: boolean;
    isAppRegistration: boolean;
    isCredential: boolean;
}

/**
 * Generic entity type check - compares entity type against a target type (case-insensitive)
 * Use this for flexible, single-import pattern: isEntityType(entityType, EntityType.Set)
 * 
 * @param entityType The entity type to check
 * @param targetType The target type to compare against (case-insensitive) - use EntityType.X for autocomplete
 * @returns true if entityType matches targetType
 * 
 * @example
 * // With EntityType enum (recommended - provides autocomplete)
 * isEntityType(entityType, EntityType.Set)
 * isEntityType(entityType, EntityType.Workflow)
 * 
 * // With string literal (still works, but no autocomplete)
 * isEntityType(entityType, 'set')
 */
export function isEntityType(entityType: string | undefined, targetType: KnownEntityType | string): boolean {
    if (!entityType) return false;
    return entityType.toLowerCase() === targetType.toLowerCase();
}

/**
 * Check if an entity type is a SetTransition
 */
export function isSetTransition(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'settransition');
}

/**
 * Check if an entity type is an EmailTemplate
 */
export function isEmailTemplate(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'emailtemplate');
}

/**
 * Check if an entity type is a RequestApprovalType
 */
export function isRequestApprovalType(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'requestapprovaltype');
}

/**
 * Check if an entity type is a PolicyRule
 */
export function isPolicyRule(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'policyrule');
}

/**
 * Check if an entity type is a Workflow
 */
export function isWorkflow(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'workflow');
}

/**
 * Check if an entity type is a WorkflowActivity
 */
export function isWorkflowActivity(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'workflowactivity');
}

/**
 * Check if an entity type is a Set
 */
export function isSet(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'set');
}

/**
 * Check if an entity type is a Group
 */
export function isGroup(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'group');
}

/**
 * Check if an entity type is a Photo
 */
export function isPhoto(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'photo');
}

/**
 * Check if an entity type is Content
 */
export function isContent(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'content');
}

/**
 * Check if an entity type is File
 */
export function isFile(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'file');
}

/**
 * Check if an entity type is a RequestApproval (runtime instance, not the config type)
 */
export function isRequestApproval(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'requestapproval');
}

/**
 * Check if an entity type is an ApiUser
 */
export function isApiUser(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'apiuser');
}

/**
 * Check if an entity type is a SpooledEmail
 */
export function isSpooledEmail(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'spooledemail');
}

/**
 * Check if an entity type is a JournalEntry
 */
export function isJournalEntry(entityType: string | undefined): boolean {
    if (!entityType) return false;
    const lower = entityType.toLowerCase().replace(/\s+/g, '');
    return lower === 'journalentry';
}

/**
 * Check if an entity type is a person type using tier1 cached entitlements (EntitySets.Person).
 * Returns false when cache is empty or Person set is missing/empty.
 */
export function isPerson(entityType: string | undefined): boolean {
    if (!entityType) return false;
    const cached = EntitlementsService.getCachedEntityEntitlements();
    const personTypes = getPersonEntityTypes(cached ?? null);
    if (personTypes.length === 0) return false;
    const normalized = entityType.trim().toLowerCase();
    return personTypes.some((t) => (t ?? '').toString().trim().toLowerCase() === normalized);
}

/**
 * Check if an entity type is an Employee
 */
export function isEmployee(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'employee');
}

/**
 * Check if an entity type is a Contractor
 */
export function isContractor(entityType: string | undefined): boolean {
    return isEntityType(entityType, 'contractor');
}

/**
 * Get all entity type flags for a given entity type.
 * isPerson uses tier1 cached entitlements (EntitySets.Person).
 */
export function getEntityTypeFlags(entityType: string | undefined): EntityTypeFlags {
    return {
        isSetTransition: isSetTransition(entityType),
        isEmailTemplate: isEmailTemplate(entityType),
        isRequestApprovalType: isRequestApprovalType(entityType),
        isPolicyRule: isPolicyRule(entityType),
        isPerson: isPerson(entityType),
        isWorkflow: isWorkflow(entityType),
        isWorkflowActivity: isWorkflowActivity(entityType),
        isSet: isSet(entityType),
        isGroup: isGroup(entityType),
        isPhoto: isPhoto(entityType),
        isContent: isContent(entityType),
        isFile: isFile(entityType),
        isJournalEntry: isJournalEntry(entityType),
        isSpooledEmail: isSpooledEmail(entityType),
        isRequestApproval: isRequestApproval(entityType),
        isApiUser: isApiUser(entityType),
        isModificationError: isEntityType(entityType, EntityType.ModificationError),
        isAppRegistration: isEntityType(entityType, EntityType.AppRegistration),
        isCredential: isEntityType(entityType, EntityType.Credential),
    };
}

/**
 * Check if entity type supports mapping (geocoding)
 */
export function supportsMapping(entityType: string | undefined): boolean {
    if (!entityType) return false;
    return ['school', 'building', 'responsibilitycenter', 'studentbusstop'].includes(entityType.toLowerCase());
}

/**
 * Fields to exclude from entity forms (system/read-only fields)
 */
export const EXCLUDED_FORM_FIELDS = new Set([
    'Id', 'id',
    'EntityType', 'entityType',
    'Meta', 'meta',
    'Schemas', 'schemas',
    'Created', 'created',
    'LastModified', 'lastModified',
    'Version', 'version'
]);

/**
 * Check if a field should be excluded from forms
 */
export function isExcludedFormField(fieldName: string): boolean {
    return EXCLUDED_FORM_FIELDS.has(fieldName);
}

